<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Sample Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="submitForm">
      <b-row class="mt-4">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase"
                >Sample Identification</span
              >
            </b-card-header>
            <div class="py-3 px-5">
              <b-form-group label="Sample Type" :label-cols="2">
                <multiselect
                  v-model="selected_sample_type"
                  :value="sample_type_value"
                  :options="sample_type_options"
                  :multiple="false"
                  :disabled="!!submission"
                  track-by="id"
                  label="name"
                />
              </b-form-group>
              <b-form-group v-if="!sample" label="Submission" :label-cols="2">
                <multiselect
                  v-model="selected_submission"
                  :value="submission_value"
                  :options="submission_options"
                  :multiple="false"
                  :disabled="!selected_sample_type || !!submission"
                  track-by="id"
                  label="name"
                />
              </b-form-group>
              <b-form-group label="Name" :label-cols="2">
                <b-form-input
                  type="text"
                  v-model="form_data.identifier"
                  placeholder="Sample Name"
                  :state="!$v.form_data.identifier.$invalid"
                />
              </b-form-group>
              <b-form-group label="Number" :label-cols="2">
                <b-form-input
                  type="number"
                  v-model="form_data.number"
                  placeholder="Sample Number"
                  :state="!$v.form_data.number.$invalid"
                />
              </b-form-group>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="selected_sample_type && ['Soil', 'Septic Soil', 'Irrigation Suitability', 'Water', 'Feed Mixer Efficiency', 'Feed'].includes(selected_sample_type.name)">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase">
                Sample Type {{ selected_sample_type.name }}
              </span>
            </b-card-header>

            <b-card-body class="py-3 px-5">
              <b-form-group v-if="selected_sample_type.name === 'Irrigation Suitability'" label="Bore Hole" :label-cols="2">
                <b-form-input
                  type="text"
                  v-model="form_data.bore_hole"
                  placeholder="Bore Hole"
                />
              </b-form-group>

              <b-form-row v-if="selected_sample_type.name === 'Soil' || selected_sample_type.name === 'Septic Soil' || selected_sample_type.name === 'Irrigation Suitability'" class="mb-3">
                <b-col cols="2">Depth</b-col>
                <b-col>
                  <b-form-input
                    type="text"
                    v-model="form_data.depth_start"
                    placeholder="Start"
                    required
                    @change="updateTotal"
                  />
                </b-col>
                <b-col>
                  <b-form-input
                    type="text"
                    v-model="form_data.depth_end"
                    placeholder="End"
                    required
                    @change="updateTotal"
                  />
                </b-col>
                <b-col>
                  <b-form-input
                    type="text"
                    :value="form_data.depth_total"
                    placeholder="Total"
                    disabled
                  />
                </b-col>
              </b-form-row>

              <template v-if="selected_sample_type.name === 'Irrigation Suitability'">
                <b-form-group label="Texture" :label-cols="2">
                  <b-form-input
                    type="text"
                    v-model="form_data.texture"
                    placeholder="Texture"
                  />
                </b-form-group>
                <b-form-group label="Horizon" :label-cols="2">
                  <b-form-input
                    type="text"
                    v-model="form_data.horizon"
                    placeholder="Horizon"
                  />
                </b-form-group>
              </template>

              <template v-if="selected_sample_type.name === 'Water'">
                <b-form-group label="Usage" :label-cols="2">
                  <b-form-input
                    type="text"
                    v-model="form_data.usage"
                    placeholder="Usage"
                  />
                </b-form-group>
              </template>

              <b-form-group v-if="selected_sample_type.name === 'Feed Mixer Efficiency'" label="Type" :label-cols="2">
                <b-form-input
                  type="text"
                  v-model="form_data.type"
                  placeholder="Type"
                />
              </b-form-group>

              <template v-if="selected_sample_type.name === 'Feed'">
                <b-form-group label="Feed Type" :label-cols="2">
                  <multiselect
                    v-model="selected_feed_type"
                    :value="feed_type_value"
                    :options="feed_type_options"
                    :multiple="false"
                    track-by="id"
                    label="name"
                  />
                </b-form-group>

                <b-form-group label="Animal Fed" :label-cols="2">
                  <multiselect
                    v-model="selected_animal_fed"
                    :value="animal_fed_value"
                    :options="animal_fed_options"
                    :multiple="false"
                    track-by="id"
                    label="name"
                  />
                </b-form-group>
              </template>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase">
                Analytes
              </span>
            </b-card-header>

            <b-card-body>
              <SampleFormAnalytes
                :sample="sample"
                :analytes="sample ? sample.analytes : []"
                :sample_type="sample_type_value"
                @existingAnalytesUpdated="(data) => { existing_analytes = data }"
                @newAnalytesUpdated="(data) => { new_analytes = data }"
              />
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="sample">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase">
                Reason for Update
              </span>
            </b-card-header>

            <b-card-body>
              <b-form-group label="Reason for Update" :label-cols="2">
                <b-form-input
                  type="text"
                  v-model="form_data.update_reason"
                  placeholder="Update Reason"
                  :state="!$v.form_data.update_reason.$invalid"
                />
              </b-form-group>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-save" /> Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import SampleCard from "@/components/lab/sample/SampleCard";
import { getCollection } from "@/shared/fetch-utils";
import SampleFormAnalytes from "@/components/lab/sample/form/SampleFormAnalytes";
import { required } from 'vuelidate/lib/validators'
import { requiredIf } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'

export default {
  name: "SampleForm",
  components: {
    SampleCard,
    Multiselect,
    SampleFormAnalytes
  },
  mixins: [validationMixin],
  props: {
    sample: {
      type: Object,
      default: () => null,
    },
    submission: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: true,
      form_data: {
        submission_id: null,
        identifier: null,
        number: null,
        update_reason: null,

        depth_start: null,
        depth_end: null,
        depth_total: null,

        bore_hole: null,
        texture: null,
        horizon: null,

        usage: null,

        type: null,

        feed_type_id: null,
        animal_fed_id: null,
      },
      sample_type_collection: null,
      sample_type_options: [],
      sample_type_value: null,
      submission_collection: null,
      submission_options: [],
      submission_value: null,
      feed_type_options: [],
      feed_type_value: null,
      animal_fed_options: [],
      animal_fed_value: null,

      managed_list_collection: null,

      // To delete existing analytes if they have delete flag set
      existing_analytes: [],

      // To add new analytes
      new_analytes: [],
    };
  },
  validations: {
    form_data: {
      submission_id: {
        required: requiredIf(function () {
          return !this.sample || !this.sample.id
        })
      },
      identifier: { required },
      number: { required },
      update_reason: {
        required: requiredIf(function () {
          return this.sample && this.sample.id
        })
      },
    },
  },
  computed: {
    selected_sample_type: {
      get() {
        return this.sample_type_value;
      },
      set(value) {
        this.sample_type_value = value;
        value && this.getSubmissions(value.id);
        this.submission_value = null;
        this.form_data.submission_id = null;

        // clear sample specific fields
        this.form_data = {
          ...this.form_data,
          depth_start: null,
          depth_end: null,
          depth_total: null,
          bore_hole: null,
          texture: null,
          horizon: null,
          usage: null,
          type: null,
          feed_type_id: null,
          animal_fed_id: null,
        }

        this.feed_type_value = null
        this.animal_fed_value = null
      },
    },
    selected_submission: {
      get() {
        return this.submission_value;
      },
      set(value) {
        this.submission_value = value;
        this.form_data.submission_id = value && value.id;
      },
    },
    selected_feed_type: {
      get() {
        return this.feed_type_value;
      },
      set(value) {
        this.feed_type_value = value;
        this.form_data.feed_type_id = value && value.id;
      },
    },
    selected_animal_fed: {
      get() {
        return this.animal_fed_value;
      },
      set(value) {
        this.animal_fed_value = value;
        this.form_data.animal_fed_id = value && value.id;
      },
    },
    invalid() {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    }
  },
  methods: {
    async getSubmissions(sample_type_id) {
      this.submission_collection = await getCollection({
        collection_type: "submission",
        filters: [
          {
            logic: "filterEquals",
            field: "sample_type_id",
            value: sample_type_id,
          },
        ],
        message: this.$bvToast,
      });

      this.submission_options = this.submission_collection.models.map(
        (submission) => {
          return {
            id: submission.id,
            name: `${submission.publicNumber()} ${submission.identifier}`,
          };
        }
      );
    },
    async initFormData() {
      const { submission } = this.sample;
      const sample_type_id = submission && submission.sample_type_id;

      if (submission && sample_type_id) {
        this.sample_type_value = this.sample_type_options.find(
          ({ id }) => id === sample_type_id
        );

        await this.getSubmissions(sample_type_id);

        this.submission_value = this.submission_options.find(
          ({ id }) => id === submission.id
        );
      }

      this.form_data = {
        ...this.sample.cloneAttributes(true, true, ["id", "pid", "update_reason"]),
      };

      if (this.sample.feed_type_id) {
        this.feed_type_value = this.feed_type_options.find(
          ({ id }) => id === this.sample.feed_type_id
        )
      }

      if (this.sample.animal_fed_id) {
        this.animal_fed_value = this.animal_fed_options.find(
          ({ id }) => id === this.sample.animal_fed_id
        )
      }
    },
    async init() {
      this.sample_type_collection = await getCollection({
        collection_type: "sample_type",
        message: this.$bvToast,
      });

      this.sample_type_options = this.sample_type_collection.models.map(
        ({ id, name }) => {
          return {
            id,
            name,
          };
        }
      );

      this.managed_list_collection = await getCollection({
        collection_type: "managed_list",
        message: this.$bvToast,
      })

      this.feed_type_options = this.managed_list_collection.find(
        (item) => item.sub_type === "feed" && item.list === "type"
      ).attribute_lists.map(({ id, value }) => {
        return {
          id,
          name: value,
        }
      })

      this.animal_fed_options = this.managed_list_collection.find(
        (item) => item.sub_type === "feed" && item.list === "animal_fed"
      ).attribute_lists.map(({ id, value }) => {
        return {
          id,
          name: value,
        }
      })

      if (this.sample) {
        this.initFormData();
      } else if (this.submission) {
        this.sample_type_value = {
          id: this.submission.sample_type_id,
          name: this.submission.sample_type.name,
        }

        this.submission_value = {
          id: this.submission.id,
          name: `${this.submission.publicNumber()} ${this.submission.identifier}`,
        }
        this.form_data.submission_id = this.submission.id
      }

      this.loading = false;
    },
    submitForm() {
      this.$v.$touch()
      if (this.invalid) return

      return this.$emit("submit", this.form_data, this.new_analytes, this.existing_analytes);
    },
    updateTotal() {
      let start = this.form_data.depth_start || 0;
      let end = this.form_data.depth_end || 0;
      this.form_data.depth_total = (end > start ? end - start : null);
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
